"use client";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Separator } from "components/ui/separator";
import { toast } from "components/ui/use-toast";
import { useReCaptcha } from "next-recaptcha-v3";
import { useEffect, useRef, useState } from "react";
import { passwordStrength as getPasswordStrength } from "check-password-strength";
import clsx from "clsx";
import { AlertCircle, InfoIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { setToken, setupAccessToken } from "lib/login";
import { Skeleton } from "components/ui/skeleton";
import Image from "next/image";
import logOut from "lib/log-out";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { initFirebase } from "lib/firebase";

type PasswordStrengths = "Too weak" | "Weak" | "Medium" | "Strong";

initFirebase();

const Page = () => {
  const [isSignUpSuccessful, setIsSignUpSuccessful] = useState<boolean>();
  const [isWaitlistAddSuccessful, setIsWaitlistAddSuccessful] =
    useState<boolean>();
  const [isNewSignUpAllowed, setIsNewSignUpAllowed] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [firstname, setFirstname] = useState<string>();
  const [lastname, setLastname] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [waitlistEmail, setWaitlistEmail] = useState<string>();
  const [countdown, setCountdown] = useState<number>(10);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrengths>();
  const [passwordConfirm, setPasswordConfirm] = useState<string>();
  const email = useRef<string>(null);

  // Import 'executeRecaptcha' using 'useReCaptcha' hook
  const { executeRecaptcha, loaded } = useReCaptcha();

  const startCountdown = () => {
    const interval = setInterval(() => {
      setCountdown((prevCount) => {
        if (prevCount === null) {
          return 10;
        }

        if (prevCount <= 0) {
          clearInterval(interval);
          return null;
        }
        return prevCount - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    const init = async () => {
      // Generate ReCaptcha token
      const token = await executeRecaptcha("page_load");

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/app-settings/is-new-signup-allowed`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
            token,
          },
        }
      );

      const data = await response.json();

      if (data.statusCode === 422) {
        // Show error to your customer (for example, payment details incomplete)
        toast({
          duration: 1000,
          variant: "destructive",
          description: data.message,
        });

        return;
      }

      setIsNewSignUpAllowed(data.isNewSignUpAllowed);
    };

    if (!executeRecaptcha || !loaded) return;

    init();
  }, [executeRecaptcha, loaded]);

  const handleResendConfirmationEmail = async () => {
    startCountdown();

    // Generate ReCaptcha token
    const token = await executeRecaptcha("form_submit");

    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/users/sign-up/re-send-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
          body: JSON.stringify({
            email: email.current,
            token,
            password,
            firstname,
            lastname,
          }),
        }
      );

      const data = await response.json();

      if (data.statusCode === 422) {
        // Show error to your customer (for example, payment details incomplete)
        toast({
          duration: 1000,
          variant: "destructive",
          description: data.message,
        });

        return;
      }

      setToken(data.token);

      toast({
        duration: 1000,
        description: "Inscription prise en compte !",
      });

      setIsSignUpSuccessful(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = ({
    target: { value },
  }: {
    target: { value: string };
  }) => {
    email.current = value;
  };

  const handleChangePassword = ({
    target: { value },
  }: {
    target: { value: string };
  }) => {
    setPassword(value);
    setPasswordStrength(getPasswordStrength(value).value as PasswordStrengths);
  };

  const handleChangePasswordConfirm = ({
    target: { value },
  }: {
    target: { value: string };
  }) => {
    setPasswordConfirm(value);
  };

  const handleChangeLastname = ({
    target: { value },
  }: {
    target: { value: string };
  }) => {
    setLastname(value);
  };

  const handleChangeFirstname = ({
    target: { value },
  }: {
    target: { value: string };
  }) => {
    setFirstname(value);
  };

  const handleConfirmSignUp = async () => {
    if (
      !email.current ||
      !email.current
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      toast({
        duration: 1000,
        description: "Email invalide",
        variant: "destructive",
      });

      return;
    }

    if (!passwordConfirm || password !== passwordConfirm) {
      toast({
        duration: 1000,
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive",
      });

      return;
    }

    // Generate ReCaptcha token
    const token = await executeRecaptcha("form_submit");

    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/users/sign-up`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
          body: JSON.stringify({
            email: email.current,
            token,
            password,
            firstname,
            lastname,
          }),
        }
      );

      const data = await response.json();

      if (data.statusCode === 422) {
        // Show error to your customer (for example, payment details incomplete)
        toast({
          duration: 1000,
          variant: "destructive",
          description: data.message,
        });

        return;
      }

      logOut();

      const auth = getAuth();

      const result = await signInWithCustomToken(auth, data.token);

      await setupAccessToken(result.user);

      toast({
        duration: 1000,
        description: "Inscription prise en compte !",
      });

      setIsSignUpSuccessful(true);
      startCountdown();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToWaitlist = async () => {
    if (
      !waitlistEmail ||
      !waitlistEmail
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      toast({
        duration: 1000,
        description: "Email invalide",
        variant: "destructive",
      });

      return;
    }

    // Generate ReCaptcha token
    const token = await executeRecaptcha("form_submit");

    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/customer-waitlist`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
          body: JSON.stringify({
            email: waitlistEmail,
            token,
          }),
        }
      );

      const data = await response.json();

      if (data.statusCode === 422) {
        // Show error to your customer (for example, payment details incomplete)
        toast({
          duration: 1000,
          variant: "destructive",
          description: data.message,
        });

        return;
      }

      setToken(data.token);

      toast({
        duration: 1000,
        description: "Tu as bien été ajouté à la liste d'attente !",
      });

      setIsWaitlistAddSuccessful(true);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSignUpForm = (isLoading?: boolean) => {
    return (
      <>
        <div className="space-y-2">
          {isLoading ? (
            <Skeleton className="h-5 w-32" />
          ) : (
            <Label htmlFor="firstname">{"Ton prénom :"}</Label>
          )}

          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Input
              id="firstname"
              onChange={handleChangeFirstname}
              placeholder="John"
            />
          )}
        </div>

        <div className="space-y-2">
          {isLoading ? (
            <Skeleton className="h-5 w-32" />
          ) : (
            <Label htmlFor="lastname">{"Ton nom :"}</Label>
          )}

          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Input
              id="lastname"
              onChange={handleChangeLastname}
              placeholder="Doe"
            />
          )}
        </div>

        <div className="space-y-2">
          {isLoading ? (
            <Skeleton className="h-5 w-32" />
          ) : (
            <Label htmlFor="email">{"Ton adresse mail :"}</Label>
          )}

          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              onChange={handleChangeEmail}
              placeholder="alice@example.com"
            />
          )}
        </div>

        <div className="space-y-2">
          {isLoading ? (
            <Skeleton className="h-5 w-32" />
          ) : (
            <Label htmlFor="password">{"Choisis un mot de passe :"}</Label>
          )}

          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Input
              type="password"
              id="password"
              name="password"
              autoComplete="new-password"
              onChange={handleChangePassword}
            />
          )}

          <div>
            <div className="flex space-x-1">
              <div
                className={clsx("w-1/3 h-2 rounded-l-sm", {
                  "bg-muted": !password,
                  "bg-destructive": passwordStrength === "Too weak",
                  "bg-warning": ["Weak", "Medium"].includes(
                    passwordStrength || ""
                  ),
                  "bg-primary": passwordStrength === "Strong",
                })}
              />
              <div
                className={clsx("w-1/3 h-2 bg-muted", {
                  "bg-muted": !password,
                  "bg-warning": ["Weak", "Medium"].includes(
                    passwordStrength || ""
                  ),
                  "bg-primary": passwordStrength === "Strong",
                })}
              />
              <div
                className={clsx("w-1/3 h-2 bg-muted rounded-r-sm", {
                  "bg-muted": !password,
                  "bg-primary": passwordStrength === "Strong",
                })}
              />
            </div>
            {password && passwordStrength === "Too weak" && (
              <p className="text-destructive text-sm">{"Trop faible"}</p>
            )}
            {password && passwordStrength === "Weak" && (
              <p className="text-warning text-sm">{"Faible"}</p>
            )}
            {password && passwordStrength === "Medium" && (
              <p className="text-warning text-sm">{"Moyen"}</p>
            )}
            {password && passwordStrength === "Strong" && (
              <p className="text-primary text-sm">{"Bien"}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {isLoading ? (
            <Skeleton className="h-5 w-32" />
          ) : (
            <Label htmlFor="password-confirm">
              {"Confirme ton mot de passe :"}
            </Label>
          )}

          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Input
              type="password"
              id="password-confirm"
              name="password_confirmation"
              autoComplete="new-password"
              onChange={handleChangePasswordConfirm}
            />
          )}

          {passwordConfirm && password !== passwordConfirm && (
            <div className="text-destructive flex items-center text-sm space-x-1">
              <InfoIcon className="w-4 h-4" />
              <p>{"Les mots de passe ne correspondent pas"}</p>
            </div>
          )}
        </div>

        {isLoading ? (
          <Skeleton className="h-10 w-full 2xl:w-1/3" />
        ) : (
          <Button
            onClick={handleConfirmSignUp}
            className="w-full 2xl:w-1/3"
            disabled={isLoading}
          >
            {"Créer mon compte"}
          </Button>
        )}
      </>
    );
  };

  const renderCannotTakeNewSignUpsForNow = () => {
    if (isWaitlistAddSuccessful) {
      return (
        <div className="flex flex-col items-center">
          <h1 className="text-xl lg:text-2xl xl:text-3xl">
            {"C'est tout bon !"}
          </h1>
          <Image
            src="/waitlist-success-state.png"
            alt={"waitlist success state"}
            width={400}
            height={400}
            className="p-2"
          />
          <p>
            {"Nous t'enverrons un email lorsqu'une place sera libre, promis."}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <Alert
          variant="default"
          className="border-warning bg-warning text-warning-foreground"
        >
          <AlertCircle className="h-4 w-4 stroke-warning-foreground" />
          <AlertTitle>{"Désolé"}</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>
              {
                "Afin de pouvoir offrir la meilleure expérience aux clients déjà inscris, nous ne prenons pas de nouveaux inscris pour l'instant."
              }
            </p>

            <p>
              {
                "Cela dit, rassure-toi, tu seras parmi les premiers à être informés lorsque les prochaines places se libéreront 👇"
              }
            </p>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="email">{"Ton adresse mail :"}</Label>
          <Input
            type="email"
            id="email"
            onChange={({ target: { value } }) => {
              setWaitlistEmail(value);
            }}
            placeholder="alice@example.com"
          />
        </div>

        <Button
          onClick={handleAddToWaitlist}
          className="w-full"
          disabled={isLoading || !waitlistEmail}
        >
          {"M'ajouter à la liste d'attente"}
        </Button>
      </div>
    );
  };

  return (
    <div className="h-screen anim_gradient">
      <div className="p-4 space-y-4 lg:w-1/3 lg:mx-auto bg-background h-full">
        <h1 className="text-2xl lg:text-3xl">{"Créer un compte Jennyz"}</h1>

        <Separator className="w-full" />

        {isSignUpSuccessful && (
          <Alert variant="default" className="border-primary text-primary">
            <AlertCircle className="h-4 w-4 stroke-primary" />
            <AlertTitle>{"Inscription est prise en compte !"}</AlertTitle>
            <AlertDescription>
              <p>
                {
                  "Un email de confirmation a été envoyé à ton adresse mail, vérifie bien les spams au cas où"
                }
              </p>
            </AlertDescription>
          </Alert>
        )}

        {isSignUpSuccessful ? (
          <div className="space-y-2">
            <p>{"Mail pas reçu ? même pas dans les spams ?"}</p>
            <Button
              onClick={handleResendConfirmationEmail}
              className="w-full"
              disabled={isLoading || countdown > 0}
            >
              {"Renvoyer le mail d'inscription"}
            </Button>
            {countdown > 0 && `Tu pourras retenter dans ${countdown}`}
          </div>
        ) : isNewSignUpAllowed === true ? (
          renderSignUpForm(false)
        ) : isNewSignUpAllowed === false ? (
          renderCannotTakeNewSignUpsForNow()
        ) : (
          renderSignUpForm(true)
        )}
      </div>
    </div>
  );
};

export default Page;
